import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { theme } from '../theme';
import { useIsMobile } from '../hooks/useMediaQuery';
import InteractiveContent from './InteractiveContent';

const inputSt = {
  width: '100%', padding: '8px 10px',
  backgroundColor: theme.colors.background,
  border: `1px solid ${theme.colors.border}`,
  borderRadius: '6px', color: theme.colors.text,
  fontSize: '0.88rem', boxSizing: 'border-box', outline: 'none',
};

function ConversationViewer({ token }) {
  const { convId } = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const bottomRef = useRef(null);

  const [messages, setMessages] = useState([]);
  const [isLive, setIsLive] = useState(false);
  const [loading, setLoading] = useState(true);

  // Admin control state
  const [controlled, setControlled] = useState(false);
  const [takingOver, setTakingOver] = useState(false);
  const [adminText, setAdminText] = useState('');
  const [sending, setSending] = useState(false);

  // Transfer modal state
  const [showTransfer, setShowTransfer] = useState(false);
  const [transferType, setTransferType] = useState('flow');
  const [flows, setFlows] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedTarget, setSelectedTarget] = useState('');
  const [transferring, setTransferring] = useState(false);
  const [transferMsg, setTransferMsg] = useState('');

  const authH = { Authorization: `Bearer ${token}` };
  const jsonH = { ...authH, 'Content-Type': 'application/json' };

  // Fetch messages (polling every 3s)
  const fetchMessages = async () => {
    try {
      const r = await fetch(`/api/monitor/${convId}/messages`, { headers: authH });
      if (!r.ok) return;
      const data = await r.json();
      setMessages(data.messages || []);
      setIsLive(data.isLive);
    } catch {}
  };

  // Check admin control status
  const checkControl = async () => {
    try {
      const r = await fetch(`/api/monitor/${convId}/control`, { headers: authH });
      if (r.ok) {
        const data = await r.json();
        setControlled(data.controlled);
      }
    } catch {}
  };

  // Carga la lista de agentes CONECTADOS (online/busy) de la empresa. El backend
  // filtra por agent_status; el company_id se deriva del convId para el admin de
  // plataforma (rol 1). Se llama al montar y al abrir el modal de transferencia
  // para que el estado online esté al día.
  const fetchOnlineAgents = () => {
    const companyIdFromConv = convId?.split('_')[0] || '';
    return fetch(`/api/monitor/agents?company_id=${companyIdFromConv}`, { headers: authH })
      .then(r => r.ok ? r.json() : { data: [] })
      .then(d => setUsers(d.data || []))
      .catch(() => {});
  };

  // Initial load
  useEffect(() => {
    setLoading(true);
    Promise.all([fetchMessages(), checkControl()]).finally(() => setLoading(false));

    // Load flows and online agents for transfer modal
    fetch('/api/flows?page=1', { headers: authH })
      .then(r => r.ok ? r.json() : { data: [] })
      .then(d => setFlows(d.data || []));
    fetchOnlineAgents();
  }, [convId, token]);

  // Polling
  useEffect(() => {
    const interval = setInterval(() => {
      fetchMessages();
      checkControl();
    }, 3000);
    return () => clearInterval(interval);
  }, [convId, token]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Takeover toggle
  const toggleTakeover = async () => {
    setTakingOver(true);
    try {
      if (!controlled) {
        await fetch(`/api/monitor/${convId}/takeover`, { method: 'POST', headers: jsonH });
        setControlled(true);
      } else {
        await fetch(`/api/monitor/${convId}/takeover`, { method: 'DELETE', headers: jsonH });
        setControlled(false);
        setAdminText('');
      }
    } catch {}
    setTakingOver(false);
  };

  // Send admin message
  const sendAdminMessage = async () => {
    if (!adminText.trim()) return;
    setSending(true);
    try {
      await fetch(`/api/monitor/${convId}/send`, {
        method: 'POST', headers: jsonH,
        body: JSON.stringify({ text: adminText.trim() }),
      });
      setAdminText('');
      await fetchMessages();
    } catch {}
    setSending(false);
  };

  // Transfer
  const handleTransfer = async () => {
    if (!selectedTarget) return;
    setTransferring(true);
    setTransferMsg('');
    try {
      const target = transferType === 'flow'
        ? flows.find(f => String(f.id) === selectedTarget)
        : users.find(u => String(u.id) === selectedTarget);
      const r = await fetch(`/api/monitor/${convId}/transfer`, {
        method: 'POST', headers: jsonH,
        body: JSON.stringify({ type: transferType, targetId: selectedTarget, targetName: target?.name || target?.username || selectedTarget }),
      });
      if (r.ok) {
        setTransferMsg('✓ Conversación transferida correctamente');
        setTimeout(() => setShowTransfer(false), 1500);
      } else {
        const j = await r.json();
        setTransferMsg(j.message || 'Error al transferir');
      }
    } catch {
      setTransferMsg('Error de conexión');
    }
    setTransferring(false);
  };

  // Parse convId for display
  const parts = convId?.split('_') || [];
  const customerPhone = parts[2] || '—';

  const formatTime = (ts) => {
    if (!ts) return '';
    try { return new Date(ts).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' }); } catch { return ''; }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)', backgroundColor: theme.colors.background }}>

      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: isMobile ? '0.8rem 1rem' : '0.8rem 1.5rem', backgroundColor: theme.colors.surface,
        borderBottom: `1px solid ${theme.colors.border}`, gap: '1rem', flexShrink: 0,
        flexWrap: isMobile ? 'wrap' : 'nowrap',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button
            onClick={() => navigate('/conversaciones')}
            style={{ background: 'none', border: `1px solid ${theme.colors.border}`, color: theme.colors.textMuted, borderRadius: '6px', padding: '5px 12px', cursor: 'pointer', fontSize: '0.82rem' }}
          >
            ← Volver
          </button>
          <div>
            <div style={{ color: theme.colors.primary, fontWeight: 700, fontSize: '0.95rem' }}>
              Conversación con {customerPhone}
            </div>
            <div style={{ color: theme.colors.textMuted, fontSize: '0.72rem' }}>
              {isLive ? '🟢 En vivo' : '🔴 Histórico'} · {convId}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button
            onClick={() => { setShowTransfer(true); setTransferMsg(''); setSelectedTarget(''); fetchOnlineAgents(); }}
            style={{
              background: 'none', border: `1px solid ${theme.colors.border}`,
              color: theme.colors.text, padding: '6px 16px', borderRadius: '7px',
              cursor: 'pointer', fontSize: '0.85rem',
            }}
          >
            Transferir
          </button>
          <button
            onClick={toggleTakeover}
            disabled={takingOver}
            style={{
              backgroundColor: controlled ? '#ef4444' : theme.colors.primary,
              color: controlled ? '#fff' : '#000',
              border: 'none', padding: '6px 16px', borderRadius: '7px',
              cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600,
            }}
          >
            {takingOver ? '...' : controlled ? 'Soltar control' : 'Tomar control'}
          </button>
        </div>
      </div>

      {/* Messages area */}
      <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '1.2rem 1rem' : '1.2rem 1.5rem', display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {loading ? (
          <p style={{ color: theme.colors.textMuted, textAlign: 'center', marginTop: '2rem' }}>Cargando mensajes...</p>
        ) : messages.length === 0 ? (
          <p style={{ color: theme.colors.textMuted, textAlign: 'center', marginTop: '2rem' }}>Sin mensajes en esta conversación.</p>
        ) : messages.map((msg, i) => {
          const isClient = msg.send === 'client';
          return (
            <div key={i} style={{ display: 'flex', justifyContent: isClient ? 'flex-end' : 'flex-start' }}>
              <div style={{
                maxWidth: isMobile ? '85%' : '65%',
                backgroundColor: isClient ? theme.colors.primary : theme.colors.surface,
                color: isClient ? '#000' : theme.colors.text,
                borderRadius: isClient ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                padding: '8px 12px',
                border: isClient ? 'none' : `1px solid ${theme.colors.border}`,
              }}>
                {msg.type === 'interactive' ? (
                  <div style={{ fontSize: '0.88rem' }}>
                    <InteractiveContent interactive={msg.interactive} body={msg.content} dark={isClient} />
                  </div>
                ) : (
                  <>
                    {msg.type === 'image' && msg.media_url ? (
                      <img src={msg.media_url} alt="imagen" style={{ maxWidth: '100%', borderRadius: '8px', marginBottom: '4px', display: 'block' }} />
                    ) : msg.type === 'audio' && msg.media_url ? (
                      <audio controls src={msg.media_url} style={{ width: '220px' }} />
                    ) : null}
                    {msg.content && (
                      <div style={{ fontSize: '0.88rem', wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>{msg.content}</div>
                    )}
                  </>
                )}
                <div style={{ fontSize: '0.68rem', color: isClient ? 'rgba(0,0,0,0.45)' : theme.colors.textMuted, marginTop: '3px', textAlign: 'right' }}>
                  {formatTime(msg.created_at)}
                  {!isClient && msg.send === 'flow' && <span style={{ marginLeft: '4px' }}>🤖</span>}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Admin control textarea */}
      {controlled && (
        <div style={{
          padding: '0.8rem 1.5rem', backgroundColor: theme.colors.surface,
          borderTop: `2px solid #ef4444`, flexShrink: 0,
        }}>
          <div style={{ color: '#ef4444', fontSize: '0.75rem', fontWeight: 600, marginBottom: '6px' }}>
            🔴 Tienes el control — el flujo y el agente están bloqueados
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <textarea
              value={adminText}
              onChange={e => setAdminText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendAdminMessage(); } }}
              placeholder="Escribe un mensaje al cliente... (Enter para enviar)"
              rows={2}
              style={{ ...inputSt, resize: 'none', flex: 1, fontFamily: 'inherit' }}
            />
            <button
              onClick={sendAdminMessage}
              disabled={sending || !adminText.trim()}
              style={{
                backgroundColor: sending || !adminText.trim() ? theme.colors.border : theme.colors.primary,
                color: '#000', border: 'none', padding: '0 20px',
                borderRadius: '7px', cursor: 'pointer', fontWeight: 600, fontSize: '0.88rem',
                alignSelf: 'stretch',
              }}
            >
              {sending ? '...' : 'Enviar'}
            </button>
          </div>
        </div>
      )}

      {/* Transfer Modal */}
      {showTransfer && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.65)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: isMobile ? '1rem' : 0 }}>
          <div style={{ backgroundColor: theme.colors.surface, borderRadius: '12px', padding: isMobile ? '1.5rem' : '2rem', width: '100%', maxWidth: '420px', border: `1px solid ${theme.colors.border}`, boxSizing: 'border-box' }}>
            <h3 style={{ color: theme.colors.primary, margin: '0 0 1.2rem' }}>Transferir conversación</h3>

            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
              {['flow', 'agent'].map(t => (
                <label key={t} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: theme.colors.text, fontSize: '0.88rem' }}>
                  <input type="radio" checked={transferType === t} onChange={() => { setTransferType(t); setSelectedTarget(''); }} style={{ accentColor: theme.colors.primary }} />
                  {t === 'flow' ? 'A un flujo' : 'A un agente'}
                </label>
              ))}
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <select
                value={selectedTarget}
                onChange={e => setSelectedTarget(e.target.value)}
                style={{ ...inputSt, cursor: 'pointer' }}
              >
                <option value="">— Seleccionar {transferType === 'flow' ? 'flujo' : 'agente'} —</option>
                {transferType === 'flow'
                  ? flows.map(f => <option key={f.id} value={String(f.id)}>{f.name}</option>)
                  : users.map(u => <option key={u.id} value={String(u.id)}>{[u.name, u.username, u.id].filter(Boolean).join(' - ')}</option>)
                }
              </select>
              {transferType === 'agent' && users.length === 0 && (
                <p style={{ color: theme.colors.textMuted, fontSize: '0.78rem', margin: '6px 0 0' }}>
                  No hay agentes conectados en este momento.
                </p>
              )}
            </div>

            {transferMsg && (
              <p style={{ color: transferMsg.startsWith('✓') ? '#22c55e' : '#ef4444', fontSize: '0.82rem', margin: '0 0 0.8rem' }}>{transferMsg}</p>
            )}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowTransfer(false)} style={{ background: 'none', border: `1px solid ${theme.colors.border}`, color: theme.colors.text, padding: '7px 16px', borderRadius: '7px', cursor: 'pointer' }}>
                Cancelar
              </button>
              <button
                onClick={handleTransfer}
                disabled={transferring || !selectedTarget}
                style={{ backgroundColor: theme.colors.primary, color: '#000', border: 'none', padding: '7px 18px', borderRadius: '7px', cursor: 'pointer', fontWeight: 600 }}
              >
                {transferring ? 'Transfiriendo...' : 'Transferir'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ConversationViewer;
